import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Public Portfolio APIs
export const portfolioAPI = {
  // Get complete portfolio data
  getPortfolio: async () => {
    try {
      const response = await apiClient.get('/portfolio');
      return response.data;
    } catch (error) {
      console.error('Error fetching portfolio:', error);
      throw error;
    }
  },

  // Submit contact form
  submitContact: async (data) => {
    try {
      const response = await apiClient.post('/contact', data);
      return response.data;
    } catch (error) {
      console.error('Error submitting contact:', error);
      throw error;
    }
  },

  // Get active theme
  getTheme: async () => {
    try {
      const response = await apiClient.get('/theme');
      return response.data;
    } catch (error) {
      console.error('Error fetching theme:', error);
      throw error;
    }
  },

  // Get layout settings
  getLayout: async () => {
    try {
      const response = await apiClient.get('/layout');
      return response.data;
    } catch (error) {
      console.error('Error fetching layout:', error);
      throw error;
    }
  },

  // Get typography settings
  getTypography: async () => {
    try {
      const response = await apiClient.get('/typography');
      return response.data;
    } catch (error) {
      console.error('Error fetching typography:', error);
      throw error;
    }
  },
};

// Admin APIs (require authentication)
export const adminAPI = {
  // Admin login
  login: async (credentials) => {
    try {
      const response = await apiClient.post('/admin/login', credentials);
      return response.data;
    } catch (error) {
      console.error('Error logging in:', error);
      throw error;
    }
  },

  // Get portfolio data (admin)
  getPortfolio: async (token) => {
    try {
      const response = await apiClient.get('/admin/portfolio', {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching admin portfolio:', error);
      throw error;
    }
  },

  // Update portfolio content
  updatePortfolio: async (data, token) => {
    try {
      const response = await apiClient.put('/admin/portfolio', data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error updating portfolio:', error);
      throw error;
    }
  },

  // Get all contact submissions
  getContacts: async (token, params = {}) => {
    try {
      const response = await apiClient.get('/admin/contacts', {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching contacts:', error);
      throw error;
    }
  },

  // Update contact status
  updateContactStatus: async (id, status, token) => {
    try {
      const response = await apiClient.patch(
        `/admin/contacts/${id}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating contact status:', error);
      throw error;
    }
  },

  // Delete contact
  deleteContact: async (id, token) => {
    try {
      const response = await apiClient.delete(`/admin/contacts/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting contact:', error);
      throw error;
    }
  },

  // Get pre-built themes
  getPrebuiltThemes: async (token) => {
    try {
      const response = await apiClient.get('/admin/themes/prebuilt', {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching pre-built themes:', error);
      throw error;
    }
  },

  // Update theme
  updateTheme: async (themeData, token) => {
    try {
      const response = await apiClient.put('/admin/theme', themeData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error updating theme:', error);
      throw error;
    }
  },

  // Get custom themes
  getCustomThemes: async (token) => {
    try {
      const response = await apiClient.get('/admin/custom-themes', {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching custom themes:', error);
      throw error;
    }
  },

  // Create custom theme
  createCustomTheme: async (themeData, token) => {
    try {
      const response = await apiClient.post('/admin/custom-themes', themeData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error creating custom theme:', error);
      throw error;
    }
  },

  // Import custom theme from JSON
  importCustomTheme: async (themeData, token) => {
    try {
      const response = await apiClient.post('/admin/custom-themes/import', themeData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error importing theme:', error);
      throw error;
    }
  },

  // Delete custom theme
  deleteCustomTheme: async (themeId, token) => {
    try {
      const response = await apiClient.delete(`/admin/custom-themes/${themeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting theme:', error);
      throw error;
    }
  },

  // Toggle theme favorite
  toggleThemeFavorite: async (themeId, token) => {
    try {
      const response = await apiClient.patch(`/admin/custom-themes/${themeId}/favorite`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error toggling favorite:', error);
      throw error;
    }
  },

  // Get predefined layouts
  getPredefinedLayouts: async (token) => {
    try {
      const response = await apiClient.get('/admin/layouts/predefined', {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching layouts:', error);
      throw error;
    }
  },

  // Update layout
  updateLayout: async (layoutData, token) => {
    try {
      const response = await apiClient.put('/admin/layout', layoutData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error updating layout:', error);
      throw error;
    }
  },

  // Get email settings
  getEmailSettings: async (token) => {
    try {
      const response = await apiClient.get('/admin/email-settings', {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching email settings:', error);
      throw error;
    }
  },

  // Update email settings
  updateEmailSettings: async (settingsData, token) => {
    try {
      const response = await apiClient.put('/admin/email-settings', settingsData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error updating email settings:', error);
      throw error;
    }
  },

  // Test email connection
  testEmailSettings: async (token) => {
    try {
      const response = await apiClient.post('/admin/email-settings/test', {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error testing email settings:', error);
      throw error;
    }
  },

  // Request OTP for password change
  requestOTP: async (username) => {
    try {
      const response = await apiClient.post('/admin/request-otp', { username });
      return response.data;
    } catch (error) {
      console.error('Error requesting OTP:', error);
      throw error;
    }
  },

  // Verify OTP
  verifyOTP: async (username, otp) => {
    try {
      const response = await apiClient.post('/admin/verify-otp', { username, otp });
      return response.data;
    } catch (error) {
      console.error('Error verifying OTP:', error);
      throw error;
    }
  },

  // Change password
  changePassword: async (data) => {
    try {
      const response = await apiClient.post('/admin/change-password', data);
      return response.data;
    } catch (error) {
      console.error('Error changing password:', error);
      throw error;
    }
  },

  // Get SMTP status
  getSMTPStatus: async () => {
    try {
      const response = await apiClient.get('/admin/smtp-status');
      return response.data;
    } catch (error) {
      console.error('Error getting SMTP status:', error);
      throw error;
    }
  },

  // Get analytics data
  getAnalytics: async (token, days = 30) => {
    try {
      const response = await apiClient.get('/admin/analytics', {
        headers: { Authorization: `Bearer ${token}` },
        params: { days },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching analytics:', error);
      throw error;
    }
  },

  // Typography settings
  getTypographySettings: async (token) => {
    try {
      const response = await apiClient.get('/admin/typography', {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching typography settings:', error);
      throw error;
    }
  },

  getTypographyPresets: async (token) => {
    try {
      const response = await apiClient.get('/admin/typography/presets', {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching typography presets:', error);
      throw error;
    }
  },

  updateTypography: async (data, token) => {
    try {
      const response = await apiClient.put('/admin/typography', data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error updating typography:', error);
      throw error;
    }
  },
};

// Visitor tracking API
export const trackingAPI = {
  trackVisit: async (data) => {
    try {
      await apiClient.post('/track-visit', data);
    } catch (error) {
      // Silently fail - don't interrupt user experience
      console.warn('Visit tracking failed:', error);
    }
  },
};

export default { portfolioAPI, adminAPI, trackingAPI };
